/**
 * 認証セッションの保持と失効。
 *
 * ADM-01 / ADM-02 / ADM-17 / ADM-23。
 *
 * ADM-17（2026-10-04 PM 決定）。トークンをブラウザの保存領域に置かない。
 *  - 更新用トークン（refresh）は、サーバーが HttpOnly Cookie で持つ。JavaScript からは
 *    読めず、扱わない（更新・ログアウトの要求に、ブラウザが自動で付ける）。
 *  - access token はこのタブのメモリだけに置く。再読み込み・新しいタブでは無くなるので、
 *    Cookie の refresh で取り直す（axiosInstance の refreshAccessToken）。
 *  - タブ間で共有するのは、トークンではない値だけ（localStorage）:
 *      adminSession       「sid:user_id」。sid はログインごとにサーバーが振る値。
 *                         1つのキーにまとめて1回で書く（下の SESSION_KEY）
 *      userRole           サーバーで確かめた役割。YAC-2026-009 PR5a から、管理画面で使える役割
 *                         （manager・operator）をカンマでつないだ並び（例 "manager,operator"）。
 *                         運転者だけ（以前の値 "driver"）は空の並びとして読む
 *      sessionGeneration  更新に成功するたびに変わる値（別のタブの更新を知るため）
 *    これで、別のタブでのログアウト・ログインし直し（PR #86 の保護）を見分ける。
 */

import { discardAllCreateRecords } from './createRecordStore';

/**
 * YAC-2026-009 PR5a。管理画面で使える役割。1つのアカウントが両方を持つこともある。
 * 運転者（driver）は管理画面の役割ではない（運転者だけのアカウントは管理画面に入れない）。
 */
export type Role = 'manager' | 'operator';
export const ADMIN_ROLES: readonly Role[] = ['manager', 'operator'];

const ROLE_KEY = 'userRole';
/**
 * セッションの識別子（「sid:user_id」）。PR #90 レビュー（4176251198）。sid と利用者を
 * 別々のキーに書くと、別のタブには書き込みごとに storage イベントが届き、1回目の
 * 不完全な識別子（例 "sid-b:"）をそのタブのセッションとして受け入れ、2回目で
 * 入れ替わりと誤認していた。1つのキーにまとめ、1回の書き込みで替える。
 */
const SESSION_KEY = 'adminSession';
const GENERATION_KEY = 'sessionGeneration';
/** 以前（ADM-17 より前）の保存形式。トークンそのものが入っている。 */
const LEGACY_TOKEN_KEYS = ['accessToken', 'refreshToken'];
/** 以前の形式のセッションの印（今は使わない）。 */
const LEGACY_MARKER_KEYS = ['sessionId', 'sessionUser'];

/** タブ間で共有する、認証に関わる保存値（storage イベントで見る）。 */
export const SESSION_MARKER_KEYS = [SESSION_KEY, ROLE_KEY];

/** このタブの access token（メモリだけ）。 */
let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * 保存した役割（サーバーで確かめたもの）。
 *  - 確かめた役割の並び（manager・operator。空のこともある＝運転者だけ）
 *  - null: 保存が無い・知らない値がある（サーバーのプロフィールで確かめ直す）
 */
export function getRoles(): Role[] | null {
  return parseRoles(safeGet(ROLE_KEY));
}

/** 保存の形（カンマでつないだ並び）を読む。"driver" は運転者だけ（管理画面の役割なし）。 */
export function parseRoles(stored: string | null): Role[] | null {
  if (stored === null || stored === '') return null;
  if (stored === 'driver') return [];
  const parts = stored.split(',');
  if (!parts.every((part) => (ADMIN_ROLES as readonly string[]).includes(part))) return null;
  return ADMIN_ROLES.filter((role) => parts.includes(role));
}

/** 役割の並びを保存の形にする（並びは manager → operator にそろえる。空なら "driver"）。 */
function serializeRoles(roles: readonly string[]): string {
  const known = ADMIN_ROLES.filter((role) => roles.includes(role));
  return known.length ? known.join(',') : 'driver';
}

/** RV-040。サーバーで確かめた役割だけを保存する。 */
export function storeRoles(roles: readonly Role[]): void {
  safeSet(ROLE_KEY, serializeRoles(roles));
}

export function hasRole(roles: readonly Role[] | null, role: Role): boolean {
  return Boolean(roles && roles.includes(role));
}

/**
 * YAC-2026-009 PR5a。このセッションで使える役割。
 *
 * サーバーは、トークンの act（そのログインで使える役割）と、DB の今の役割（プロフィールの roles）の
 * 共通部分で判定し、運営は amr に otp（2段階認証）が無ければ効かせない（api/authentication.py）。
 * 画面の出し分けも同じ規則にする（判定そのものはサーバーが行う。ここは表示のため）。
 * act が無い・読めない access では act での絞り込みをしない（運営は amr の otp が要る）。
 */
export function actingRoles(access: string | null, held: unknown): Role[] {
  if (!Array.isArray(held)) return [];
  const payload = decodePayload(access);
  const act = Array.isArray(payload?.act) ? (payload?.act as unknown[]) : null;
  const amr = Array.isArray(payload?.amr) ? (payload?.amr as unknown[]) : null;
  return ADMIN_ROLES.filter((role) => {
    if (!held.includes(role)) return false;
    if (act && !act.includes(role)) return false;
    // 運営は、2段階認証を済ませた（amr に otp がある）ログインでだけ効く。amr が無ければ効かない。
    if (role === 'operator' && !(amr && amr.includes('otp'))) return false;
    return true;
  });
}

/**
 * ログインで得た access を、このタブのセッションとして使い始める。
 *
 * access の sid（ログインごとにサーバーが振る値）と user_id を、タブ間で共有する
 * セッションの印として保存する。access 自体は保存しない。
 */
export function storeSession(access: string, roles?: readonly Role[] | string): void {
  const identity = identityOfAccess(access);
  accessToken = access;
  // 識別子は1回の書き込みで替える（別のタブに不完全な値を見せない）。
  if (identity) safeSet(SESSION_KEY, identity);
  else safeRemove(SESSION_KEY);
  // 役割は確かめたものを並びで渡す。文字列は保存の形のまま書く（確かめ直しの試験で、知らない値を置くため）。
  if (typeof roles === 'string') {
    if (roles) safeSet(ROLE_KEY, roles);
  } else if (roles) {
    storeRoles(roles);
  }
  tabSession = getSessionIdentity(); // このタブでログインしたセッション
}

/** 更新で得た access をメモリに置く（同じセッションかは呼び出し側が確かめる）。 */
export function storeAccess(access: string): void {
  accessToken = access;
}

/**
 * 今のセッション（タブ間で共有している印）を表す値「sid:user_id」。印が無ければ null。
 */
export function getSessionIdentity(): string | null {
  return safeGet(SESSION_KEY);
}

/** sid と利用者の両方がそろった識別子か。 */
function isCompleteIdentity(identity: string | null): boolean {
  if (!identity) return false;
  const index = identity.indexOf(':');
  return index > 0 && index < identity.length - 1;
}

/** access が属するセッション（sid と user_id）。どちらかが無ければ null。 */
export function identityOfAccess(access: string | null): string | null {
  const payload = decodePayload(access);
  const sid = payload?.sid;
  const userId = payload?.user_id;
  if (sid === undefined || sid === null || userId === undefined || userId === null) return null;
  return `${sid}:${userId}`;
}

/** セッションIDか利用者が分からない（タブ間で見分けられない）セッション。 */
function hasUnidentifiableSession(): boolean {
  return !isCompleteIdentity(getSessionIdentity());
}

/** 更新の世代。どのタブかが更新に成功するたびに変わる。 */
export function getSessionGeneration(): string | null {
  return safeGet(GENERATION_KEY);
}

/** このタブの更新が成功した。別のタブへ「Cookie が新しくなった」ことを伝える。 */
export function markSessionRefreshed(): void {
  safeSet(GENERATION_KEY, newNonce());
}

function newNonce(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* 下の方法で作る */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** ADM-01。ログアウトと失効で、認証に関わる値（メモリと印）をすべて捨てる。 */
export function clearSession(): void {
  tabSession = null;
  accessToken = null;
  [...LEGACY_TOKEN_KEYS, ...LEGACY_MARKER_KEYS, SESSION_KEY, ROLE_KEY, GENERATION_KEY].forEach(
    safeRemove
  );
  // RV-130・RV-132。作成（配送先・作業マスタ・機器・車両）で結果が分からなかった値も、
  // 利用者とともに捨てる（sessionStorage とメモリの両方）。
  discardAllCreateRecords();
}

/**
 * ADM-17。以前の保存形式（localStorage の access/refresh）を捨てる。
 *
 * HttpOnly Cookie へは JavaScript から移せない（移すには refresh を本文で受け取る
 * 経路をサーバーに残す必要がある）ため、移さずに捨て、ログインし直してもらう。
 * 以前の形式のセッションIDは今の形式と意味が違うので、印・役割ごと捨てる。
 * 捨てたものがあれば true。
 */
export function discardLegacyTokens(): boolean {
  const found = [...LEGACY_TOKEN_KEYS, ...LEGACY_MARKER_KEYS].some((key) => safeGet(key) !== null);
  if (!found) return false;
  [...LEGACY_TOKEN_KEYS, ...LEGACY_MARKER_KEYS, SESSION_KEY, ROLE_KEY, GENERATION_KEY].forEach(
    safeRemove
  );
  return true;
}

const LOGOUT_UNCONFIRMED_KEY = 'logoutRevocationUnconfirmed';

/**
 * ADM-23-AC2。ログアウトでサーバー側の失効を確認できなかったことを、
 * 移動先のログイン画面へ伝える（同じタブの間だけ残す）。
 */
export function markLogoutUnconfirmed(): void {
  try {
    sessionStorage.setItem(LOGOUT_UNCONFIRMED_KEY, '1');
  } catch {
    /* 保存できない環境では伝えられない */
  }
}

/**
 * EV-352。記録があれば true（消さない）。ログインの画面は state の初期化でこれを読み、描画の後に
 * clearLogoutUnconfirmed で消す（開発の StrictMode では初期化が2回走るので、初期化の中で消すと2回目が false）。
 */
export function peekLogoutUnconfirmed(): boolean {
  try {
    return sessionStorage.getItem(LOGOUT_UNCONFIRMED_KEY) === '1';
  } catch {
    return false;
  }
}

/** EV-352。記録を消す（表示は1回だけ）。 */
export function clearLogoutUnconfirmed(): void {
  try {
    sessionStorage.removeItem(LOGOUT_UNCONFIRMED_KEY);
  } catch {
    /* 消せない環境では何もしない */
  }
}

/** 記録があれば true を返し、記録を消す（表示は1回だけ）。 */
export function consumeLogoutUnconfirmed(): boolean {
  try {
    const marked = sessionStorage.getItem(LOGOUT_UNCONFIRMED_KEY) === '1';
    sessionStorage.removeItem(LOGOUT_UNCONFIRMED_KEY);
    return marked;
  } catch {
    return false;
  }
}

/**
 * JWTの有効期限を読む。読めない場合は null。
 *
 * 署名は検証しない。検証はサーバーの責務で、ここでの目的は
 * 「明らかに期限切れのtokenで要求を送らない」ことだけ。
 */
export function getExpiry(token: string | null): number | null {
  const exp = decodePayload(token)?.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

/** JWT の payload を読む。読めなければ null（署名は検証しない）。 */
function decodePayload(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      decodeURIComponent(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join('')
      )
    );
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

export function isExpired(token: string | null, now: number = Date.now()): boolean {
  const expiry = getExpiry(token);
  if (expiry === null) return false; // 判定できない形式は期限切れ扱いにしない
  return expiry <= now;
}

/** このタブのメモリに、期限内の access があるか。 */
export function hasUsableAccess(now: number = Date.now()): boolean {
  return Boolean(accessToken) && !isExpired(accessToken, now);
}

/**
 * 画面を開いてよいか。
 *
 * ADM-01 / ADM-17。access が無い・期限切れでも、セッションの印があれば Cookie の
 * refresh で取り直せる見込みがある（取り直せなければその時点でログイン画面へ）。
 * 印が無い・見分けられないときだけ未認証として扱う。
 */
export function hasUsableSession(_now: number = Date.now()): boolean {
  if (getSessionIdentity() === null) return false;
  // 見分けられないセッションは、別のタブでの入れ替わりを検知できないので使わない。
  return !hasUnidentifiableSession();
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 保存できない環境では、そのタブ限りの動作になる */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* 保存領域が使えない場合は何もしない */
  }
}

// 読み込み時に、以前の形式のトークンを捨てる（ADM-17 の移行）。
discardLegacyTokens();

/**
 * このタブが使っているセッション。
 *
 * セッションの印はタブ間で共有なので、別のタブでログアウト・ログインし直すと、
 * このタブで準備した要求が別のセッション（別の利用者かもしれない）で送られてしまう。
 * このタブを開いたとき（またはこのタブでログインしたとき）のセッションを覚えておき、
 * 保存中のものと比べる。
 */
let tabSession: string | null = getSessionIdentity();

/**
 * 保存中のセッションが、このタブのセッションから入れ替わったか。
 *
 * このタブにセッションが無かった（ログイン前）場合は、保存中のものを
 * このタブのセッションとして受け入れる。保存中のものが無い（別のタブで
 * ログアウトした）場合は、countLogout のときだけ入れ替わりとして扱う
 * （印が無ければ要求に認証は付かないので、送信前の確認では止めない）。
 */
export function isSessionReplaced({ countLogout = false } = {}): boolean {
  const current = getSessionIdentity();
  if (tabSession === null) {
    // 不完全な識別子はこのタブのセッションとして受け入れない（そろってから受け入れる）。
    tabSession = isCompleteIdentity(current) ? current : null;
    return false;
  }
  if (current === null) return countLogout;
  return current !== tabSession;
}
